import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { AssetIoTSlaStatus } from '../graphql/queries/assetIoTSlaStatus';
import { LatestIotCommissioningEvent } from '../graphql/queries/latestIotCommissioningEvent';
import { isCommissioningSlaStatus } from '../config/iotSlaConfig';
import { isSlaEnabled } from '../utils/slaEnabled';
import { readDevSlaOverride } from '../utils/devSlaOverride';

interface Options {
    assetLocationId: string | undefined | null;
    /** Asset id (from the hero query) — needed for the commissioning event lookup. */
    assetId: string | undefined | null;
    /** Only ask for a live, non-semiconductor device and an IoT admin (the only viewer of the SLA). */
    enabled: boolean;
    /**
     * SLA values from the search index (IBM-1053), shown until the live IoT answer
     * arrives; `undefined` while the index has not answered yet.
     */
    indexFallback?: { status: string | null; level: string | null; connectivityQuality: string | null } | null;
}

export interface AssetSlaStatus {
    /** `slaInformation.status`; null while loading or when IoT has no SLA record for the device. */
    status: string | null;
    level: string | null;
    connectivityQuality: string | null;
    /** `insert` of the newest ASSET_IOT_COMMISSIONING event; only looked up while the SLA is INACTIVE / EVALUATION_FAILED. */
    lastCommissioningAt: string | null;
    /** The event lookup has answered or was not needed — until then the hero holds back the SLA line. */
    commissioningLookupSettled: boolean;
    /** Everything the SLA line and the commissioning CTA depend on has answered (or was not asked). */
    settled: boolean;
    loading: boolean;
    refetch: () => void;
}

/**
 * SLA status of the asset's IoT connection plus the newest commissioning
 * event (IBM-1045 / IBM-1041). Nothing is requested while the SLA flag is
 * off. The SLA query is no-cache + errorPolicy all because the IoT resolver
 * nulls the whole connection for devices without an SLA record (see
 * AssetIoTSlaStatus); unknown therefore simply reads as "no status".
 */
export const useAssetSlaStatus = ({ assetLocationId, assetId, enabled, indexFallback }: Options): AssetSlaStatus => {
    // Dev-only `?slaStatus=…` override (see devSlaOverride.ts) replaces the IoT answer.
    const override = readDevSlaOverride(useLocation().search);
    const asked = isSlaEnabled() && enabled && !!assetLocationId && !override;
    const slaQuery = useQuery(AssetIoTSlaStatus, {
        variables: { id: assetLocationId },
        skip: !asked,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });
    // The live answer wins once it is there — also when it is "no SLA record" (null).
    // Until then the index paints first; it can lag behind, never the other way round.
    const liveAnswered = !asked || !!slaQuery.data || (!slaQuery.loading && !!slaQuery.error);
    const fallback = asked && !liveAnswered && indexFallback?.status ? indexFallback : null;
    const info = override ?? (liveAnswered ? slaQuery.data?.assetLocation?.iotConnection?.slaInformation : fallback) ?? null;
    const status: string | null = info?.status ?? null;

    // INACTIVE / EVALUATION_FAILED need the newest commissioning event before the SLA line is shown. The
    // asset id arrives with the hero query — until then the lookup cannot even start,
    // so "unsettled" also covers that window (otherwise the "Commissioning required"
    // hint would flash before the "submitted" line).
    const eventNeeded = isSlaEnabled() && enabled && isCommissioningSlaStatus(status);
    const eventQuery = useQuery(LatestIotCommissioningEvent, {
        variables: { assetId },
        skip: !eventNeeded || !assetId,
        fetchPolicy: 'cache-and-network',
    });
    // Settled: answered (cached data counts, a refetch keeps the last answer) or failed.
    const commissioningLookupSettled =
        !eventNeeded || (!!assetId && (!!eventQuery.data || (!eventQuery.loading && !!eventQuery.error)));

    return {
        status,
        level: info?.level ?? null,
        connectivityQuality: override ? null : info?.connectivityQuality ?? null,
        lastCommissioningAt: eventQuery.data?.installedBaseEventList?.installedBaseEvents?.[0]?.insert ?? null,
        commissioningLookupSettled,
        // `loading` stays false on refetch (no notifyOnNetworkStatusChange), so a refetch never un-settles.
        // An index value settles early, except INACTIVE / EVALUATION_FAILED: those offer the commissioning, so the
        // live answer must confirm it first (a lagging index must not flash the CTA).
        settled:
            (!asked || !slaQuery.loading || (!!fallback && !isCommissioningSlaStatus(fallback.status))) &&
            commissioningLookupSettled,
        loading: asked && slaQuery.loading && !slaQuery.data && !fallback,
        refetch: () => {
            if (asked) void slaQuery.refetch();
            if (eventQuery.called) void eventQuery.refetch();
        },
    };
};
